import "./App.css";
import "./index.css";

import { useAuth0 } from "@auth0/auth0-react";
import { Alert, CssBaseline, createTheme, ThemeProvider } from "@mui/material";
import type { AlertColor } from "@mui/material/Alert/Alert";
import Snackbar from "@mui/material/Snackbar";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useState } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
} from "react-router-dom";

import { Backend, getBackend, USE_AUTH } from "./api/apiConfig";
import { PlaidOAuthReturn } from "./components/CredentialsDashboard/PlaidOAuthReturn";
import MaintenancePage from "./components/MaintenancePage";
import NoOrganizationPage from "./components/NoOrganizationPage";
import { PatientPortal } from "./components/PatientPortal";
import SelectOrganizationPage from "./components/SelectOrganizationPage";
import FCPortal from "./FCPortal";
import { isDemoMode } from "./utils/utils";

function Auth0ReturnToHandler() {
  const navigate = useNavigate();
  React.useEffect(() => {
    if (window.__auth0ReturnTo) {
      navigate(window.__auth0ReturnTo, { replace: true });
      window.__auth0ReturnTo = undefined;
    }
  }, [navigate]);
  return null;
}

function InviteRedirect() {
  const { loginWithRedirect } = useAuth0();

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding loginWithRedirect would change when this runs
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invitation = params.get("invitation");
    const organization = params.get("organization");
    const orgName = params.get("org_name");

    if (invitation && organization) {
      loginWithRedirect({
        authorizationParams: {
          invitation,
          organization,
          org_name: orgName,
        },
      });
    }
  }, []);

  return null;
}

// Visual distinction so we don't accidentally do things in the wrong env.
// Keyed by enum, not positional, so adding a Backend member fails the build.
const BACKGROUND_BY_BACKEND: Record<Backend, string> = {
  [Backend.Local]: "#ffc0cb",
  [Backend.Staging]: "#cbc0ff",
  [Backend.Demo]: "#ffffff",
  [Backend.Production]: "#ffffff",
};

const BACKGROUND_COLOR = isDemoMode()
  ? "#ffffff"
  : BACKGROUND_BY_BACKEND[getBackend()];

document.body.style.backgroundColor = BACKGROUND_COLOR;

const theme = createTheme({
  palette: {
    background: {
      default: BACKGROUND_COLOR,
    },
    primary: {
      main: "#25233F",
    },
    secondary: {
      main: "#E9D7D9",
    },
    success: {
      main: "#12B76A",
    },
  },
  typography: {
    fontFamily: ["Inter Regular", "Inter", "Roboto"].join(","),
    fontSize: 14,
  },
  transitions: {
    duration: {
      leavingScreen: 700,
      enteringScreen: 700,
    },
  },
  components: {
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: BACKGROUND_COLOR,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedSuccess: {
          color: "#FFF",
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          fontSize: "14px",
          textTransform: "none",
          fontWeight: 500,
          backgroundColor: "#EAECF0",
          padding: "6px 16px",
          color: "#475467",
          "&.Mui-selected": {
            backgroundColor: "#25233F",
            color: "#FFF",
          },
          "&.Mui-selected:hover": {
            backgroundColor: "#25233F",
            color: "#FFF",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          padding: "4px",
          borderRadius: "8px",
          backgroundColor: "#EEE",
        },
        indicator: {
          display: "none",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          color: "#09080F",
          padding: "8px 16px",
          "&.Mui-selected": {
            color: "#FFF",
            backgroundColor: "#4A4A62",
          },
        },
      },
    },
  },
});

function OrganizationGuard({
  children,
}: {
  children: React.ReactElement;
}): React.ReactElement {
  const { isAuthenticated, isLoading, user, error } = useAuth0();

  if (USE_AUTH && !isLoading) {
    // Authenticated but token has no org_id claim
    if (isAuthenticated && !user?.org_id) {
      return <NoOrganizationPage />;
    }
    // Auth0 rejected the token exchange with an org membership error.
    // Show the page instead of letting withAuthenticationRequired call
    // loginWithRedirect() again, which causes an infinite redirect loop.
    if (!isAuthenticated && error?.message.includes("organization")) {
      return <NoOrganizationPage />;
    }
  }
  return children;
}

export default function App() {
  const [snackbar, updateSnackBar] = useState<{
    severity: AlertColor;
    message: string;
  } | null>(null);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Router>
          <Auth0ReturnToHandler />
          <Routes>
            <Route
              path="/patient-portal/:sessionId/*"
              element={<PatientPortal updateSnackBar={updateSnackBar} />}
            />
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route
              path="/select-organization"
              element={<SelectOrganizationPage />}
            />
            <Route path="/invite" element={<InviteRedirect />} />
            <Route
              path="/plaid-oauth"
              element={<PlaidOAuthReturn updateSnackBar={updateSnackBar} />}
            />
            {/* This is the main route for the app (FC Portal). It will render the left nav and the main view. */}
            <Route
              path="*"
              element={
                <OrganizationGuard>
                  <FCPortal updateSnackBar={updateSnackBar} />
                </OrganizationGuard>
              }
            />
          </Routes>
        </Router>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={!!snackbar}
          autoHideDuration={snackbar?.severity === "error" ? null : 4000}
          onClose={(_event, reason) => {
            if (reason === "clickaway") return;
            updateSnackBar(null);
          }}
        >
          {snackbar ? (
            <Alert
              onClose={() => {
                updateSnackBar(null);
              }}
              severity={snackbar ? snackbar.severity : undefined}
              sx={{ width: "100%" }}
            >
              {snackbar ? snackbar.message : ""}
            </Alert>
          ) : (
            <div />
          )}
        </Snackbar>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
